import React, { useState, useMemo, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatToPKR, displayDate } from "@/lib/helper";
import { ChevronDownIcon, Search, Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import Combobox from "@/components/ui/common/combobox";
import { FacilitySlot } from "@/types/facility/booking";
import { FacilitySchedule } from "@/types/facility/schedule";
import { FacilityAddOnType } from "@/types/enums";
import { Facility } from "@/types/facility";
import { MemberTableDatatypes } from "@/types/member";
import { toast } from "@/components/ui/use-toast";
import { useGetAllFacilityScheduleSlotQuery } from "@/services/facilityApis";
import { useGetFacilityScheduleByIdQuery } from "@/services/facilityApis";
import { SlotSelector } from "@/components/ui/common/slot-selector";
import { useCriteriaValidationQuery } from "@/services/facilityApis";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { calculateTaxFromTaxGroups } from "@/lib/tax-utils";

interface FacilitySlotsProps {
  orgId: number;
  selectedMember: MemberTableDatatypes | null;
  onAddToCart: (slotData: any) => void;
  cartedSlotIds?: number[];
  orgTaxType?: "inclusive" | "exclusive";
}

// Extended member type for slot selector
interface ExtendedMember
  extends Omit<MemberTableDatatypes, "membership_plans"> {
  membership_plans?: Array<{
    status: string;
    contract_id: number;
    membership_plan_id: number;
    membership_plan_name: string;
  }>;
}

interface FacilitySlotCartItem {
  slot: FacilitySlot;
  schedule: FacilitySchedule;
  selectedAddOns: Array<{
    id: number;
    name: string;
    price: number;
    type: string;
    quantity: number;
  }>;
  totalPrice: number;
  subtotal: number;
  taxAmount: number;
  finalTotal: number;
  incomeCategoryId: number;
  taxRate: number;
  taxName: string;
}

const FacilitySlots: React.FC<FacilitySlotsProps> = ({
  orgId,
  selectedMember,
  onAddToCart,
  cartedSlotIds = [],
  orgTaxType = "exclusive",
}) => {
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null,
  );
  const [selectedSlot, setSelectedSlot] = useState<FacilitySlot | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // Transform facilities for combobox
  const [slotOpen, setSlotOpen] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<
    Array<{
      id: number;
      name: string;
      price: number;
      type: string;
      quantity: number;
    }>
  >([]);

  // Fetch facilities
  const { data: facilitiesData } = useGetFacilitiesQuery({
    orgId,
    query: "status=active&bookable=true&only_available_slots=true",
  });

  // Fetch available slots for selected facility
  const { data: slotsData, isLoading: slotsLoading } =
    useGetAllFacilityScheduleSlotQuery(
      {
        orgId,
        query: useMemo(() => {
          const now = new Date();
          const isCurrentMonth =
            currentMonth.getMonth() === now.getMonth() &&
            currentMonth.getFullYear() === now.getFullYear();

          const startDate = isCurrentMonth
            ? format(now, "yyyy-MM-dd")
            : format(startOfMonth(currentMonth), "yyyy-MM-dd");
          const endDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");

          return `facility_id=${selectedFacility?.id}&type=facility_schedule&load_available=true&start_date=${startDate}&end_date=${endDate}`;
        }, [selectedFacility?.id, currentMonth]),
      },
      {
        skip: !selectedFacility?.id,
      },
    );

  // Fetch schedule details when facility is selected (using selected slot's schedule_id)
  const { data: scheduleData } = useGetFacilityScheduleByIdQuery(
    {
      org_id: orgId,
      schedule_id: selectedSlot?.facility_schedule_id as number,
      query: "type=facility_schedule&load_add_ons=true",
    },
    {
      skip: !selectedSlot?.facility_schedule_id,
    },
  );

  // Member validation for facility
  const { data: validationData, error: validationError } =
    useCriteriaValidationQuery(
      {
        org_id: orgId,
        member_id: selectedMember?.id || 0,
        facility_id: selectedFacility?.id || 0,
      },
      {
        skip: !selectedMember?.id || !selectedFacility?.id,
      },
    );

  const facilities = useMemo(
    () => facilitiesData?.data || [],
    [facilitiesData],
  );

  // Transform facilities for combobox
  const facilityOptions = useMemo(
    () =>
      facilities
        .filter((facility) => facility.id !== undefined)
        .map((facility) => ({
          label: facility.name,
          value: facility.id!.toString(),
        })),
    [facilities],
  );

  // Transform member to ExtendedMember format for slot selector
  const extendedMember: ExtendedMember | null = useMemo(() => {
    if (!selectedMember) return null;
    return {
      ...selectedMember,
      membership_plans: selectedMember.membership_plans?.map((plan) => ({
        status: "active", // Default status for POS context
        contract_id: plan.id,
        membership_plan_id: plan.membership_plan_id,
        membership_plan_name: String(plan.membership_plan_name || plan.name),
      })),
    };
  }, [selectedMember]);

  const schedule = scheduleData;
  const availableSlots: FacilitySlot[] = useMemo((): FacilitySlot[] => {
    if (!slotsData?.data) return [];

    // If we don't have schedule data yet, create basic slot info from the API response
    if (!schedule) {
      return slotsData?.data.map((slot: any) => {
        // Normalize date to ensure consistent formatting
        const normalizedDate = format(new Date(slot.slot_date), "yyyy-MM-dd");
        return {
          id: slot.id,
          price: slot.price || 0,
          facility_schedule_id: slot.facility_schedule_id || 0,
          slot_date: normalizedDate,
          start_time: slot.start_time,
          end_time: slot.end_time,
          always_available: slot.always_available || false,
          duration_hours: slot.duration_hours,
          is_exclusive: slot.is_exclusive || false,
          total_attendees: slot.consumed_slots?.length || 0,
          status: null,
          is_exclusive_schedule: slot.is_exclusive || false,
          is_available: true, // Always show available slots from API
          plan_pricing: [], // Will be populated when schedule loads
          special_pricing: [], // Will be populated when schedule loads
          tier_pricing: [],
          refund_policies: [],
        };
      }) as FacilitySlot[];
    }

    // If we have schedule data, use it for complete pricing information
    return slotsData?.data.map((slot: any) => {
      // Normalize date to ensure consistent formatting
      const normalizedDate = format(new Date(slot.slot_date), "yyyy-MM-dd");
      return {
        id: slot.id,
        price: slot.price || scheduleData.general_info.price || 0,
        facility_schedule_id: slot.facility_schedule_id || 0,
        slot_date: normalizedDate,
        start_time: slot.start_time,
        end_time: slot.end_time,
        always_available: slot.always_available || false,
        duration_hours: slot.duration_hours,
        is_exclusive: slot.is_exclusive || false,
        total_attendees: slot.consumed_slots?.length || 0,
        status: null,
        is_exclusive_schedule: slot.is_exclusive || false,
        is_available: true, // Always show available slots from API
        plan_pricing: scheduleData.plan_pricing?.map((plan) => ({
          group_id: plan.group_id,
          membership_ids: plan.membership_ids,
          price: plan.price,
        })),
        special_pricing: scheduleData.special_pricing?.map((special) => ({
          id: special.name.length, // temporary id
          name: special.name,
          date_from: special.date_from,
          date_to: special.date_to,
          price: special.price,
        })),
        tier_pricing: scheduleData.tier_pricing,
        refund_policies: scheduleData.refund_policies?.map((policy) => ({
          id: policy.cancellation_period.length, // temporary id
          cancellation_period: policy.cancellation_period,
          refund_percentage: policy.refund_percentage,
        })),
      };
    }) as FacilitySlot[];
  }, [slotsData, schedule]);

  // Derived selected slot that has the latest data (i.e. pricing from schedule)
  // This is crucial because 'selectedSlot' state holds the object from when it was clicked (before schedule fetch)
  const detailedSelectedSlot = useMemo(() => {
    if (!selectedSlot) return null;
    return availableSlots.find((s) => s.id === selectedSlot.id) || selectedSlot;
  }, [selectedSlot, availableSlots]);

  // Effect to automatically select inclusive add-ons when schedule loads
  React.useEffect(() => {
    if (schedule?.add_ons) {
      const inclusiveAddOns = schedule.add_ons
        .filter((addon) => addon.type === FacilityAddOnType.Inclusive)
        .map((addon) => ({
          id: addon.id,
          name: addon.name,
          price: addon.price,
          type: addon.type,
          quantity: 1,
        }));

      if (inclusiveAddOns.length > 0) {
        setSelectedAddOns((prev) => {
          // Keep existing non-inclusive selections, and merge with all inclusive ones
          const nonInclusiveAddOns = prev.filter(
            (item) => item.type !== FacilityAddOnType.Inclusive,
          );
          return [...nonInclusiveAddOns, ...inclusiveAddOns];
        });
      } else {
        // If there are no inclusive add-ons in the new schedule, just clear inclusive ones from selection
        setSelectedAddOns((prev) =>
          prev.filter((item) => item.type !== FacilityAddOnType.Inclusive),
        );
      }
    }
  }, [schedule, detailedSelectedSlot]);

  // Calculate pricing for selected slot
  const calculateSlotPricing = useCallback(() => {
    // Only proceed if we have both the slot and the schedule (which implies add-ons and pricing rules are loaded)
    if (!detailedSelectedSlot || !schedule) return null;

    // Base price calculation (same as slot-selector)
    const regularPrice = schedule.general_info?.price || 0;

    // Collect all applicable prices
    const prices: Array<{
      price: number;
      type: "base" | "special" | "plan" | "group";
      name?: string;
    }> = [{ price: regularPrice, type: "base" }];

    // Check Special Pricing
    if (
      detailedSelectedSlot.special_pricing &&
      detailedSelectedSlot.special_pricing.length > 0
    ) {
      const slotDate = new Date(detailedSelectedSlot.slot_date)
        .toISOString()
        .split("T")[0];

      const applicableSpecialPrices =
        detailedSelectedSlot.special_pricing.filter(
          (sp) => slotDate >= sp.date_from && slotDate <= sp.date_to,
        );

      if (applicableSpecialPrices.length > 0) {
        const lowestSP = applicableSpecialPrices.reduce((min, curr) =>
          curr.price < min.price ? curr : min,
        );
        prices.push({
          price: lowestSP.price,
          type: "special",
          name: lowestSP.name,
        });
      }
    }

    // Check Plan Pricing
    if (
      selectedMember &&
      detailedSelectedSlot.plan_pricing &&
      detailedSelectedSlot.plan_pricing.length > 0
    ) {
      const memberPlans = selectedMember.membership_plans || [];

      if (memberPlans.length > 0) {
        const customerPlanIds = memberPlans.map((mp) => mp.membership_plan_id);
        const applicablePlanPrices = detailedSelectedSlot.plan_pricing.filter(
          (pp) =>
            pp.membership_ids?.some((id) =>
              customerPlanIds.includes(Number(id)),
            ),
        );

        if (applicablePlanPrices.length > 0) {
          const lowestPP = applicablePlanPrices.reduce((min, curr) =>
            curr.price < min.price ? curr : min,
          );
          // Find the name of the plan that gave the best price
          const bestPlan = memberPlans.find((mp) =>
            detailedSelectedSlot.plan_pricing?.some(
              (pp) =>
                pp.price === lowestPP.price &&
                pp.membership_ids?.includes(Number(mp.membership_plan_id)),
            ),
          );

          prices.push({
            price: lowestPP.price,
            type: "plan",
            name: bestPlan
              ? String(bestPlan.membership_plan_name || bestPlan.name)
              : "Plan",
          });
        }
      }
    }

    // Check Group Pricing
    if (selectedMember?.member_group_id && schedule?.group_pricing) {
      const applicableGroupPrices = schedule.group_pricing.filter(
        (gp) => Number(gp.group_id) === Number(selectedMember.member_group_id),
      );

      if (applicableGroupPrices.length > 0) {
        const lowestGP = applicableGroupPrices.reduce((min, curr) =>
          curr.price < min.price ? curr : min,
        );
        prices.push({
          price: lowestGP.price,
          type: "group",
          name: "Group",
        });
      }
    }

    // Find the absolute lowest price
    const bestPricing = prices.reduce((min, curr) => {
      if (curr.price < min.price) return curr;
      return min;
    }, prices[0]);

    const basePrice = bestPricing.price;
    const priceType = bestPricing.type;
    const appliedPricingName = bestPricing.name || "";

    // Add-ons total
    const addOnsTotal = selectedAddOns.reduce(
      (total, addOn) => total + addOn.price * addOn.quantity,
      0,
    );

    // Subtotal (base price + add-ons)
    const subtotal = basePrice + addOnsTotal;

    // Tax calculation using the same logic as main sell component
    const facilityTaxGroup = schedule.general_info?.tax_group;
    // Handle tax_group as array or single object for member
    const memberTaxGroup = selectedMember?.tax_group;

    const {
      tax: selectedTax,
      taxRate,
      taxName,
    } = calculateTaxFromTaxGroups(facilityTaxGroup, memberTaxGroup || null);

    const taxAmount =
      orgTaxType === "inclusive"
        ? subtotal - subtotal / (1 + taxRate / 100)
        : (subtotal * taxRate) / 100;

    // Final total
    const finalTotal =
      orgTaxType === "inclusive" ? subtotal : subtotal + taxAmount;

    return {
      basePrice,
      addOnsTotal,
      subtotal,
      taxRate,
      taxAmount,
      taxName,
      finalTotal,
      priceType,
      appliedPricingName,
    };
  }, [detailedSelectedSlot, schedule, selectedMember, selectedAddOns]);

  const pricing = calculateSlotPricing();

  const handleFacilitySelect = (facility: Facility) => {
    setSelectedFacility(facility);
    setSelectedSlot(null);
    setSelectedAddOns([]);
  };

  const handleSlotSelect = (slot: FacilitySlot) => {
    // Validate member first
    if (!selectedMember) {
      toast({
        variant: "destructive",
        title: "Member required",
        description: "Please select a member before selecting a facility slot",
      });
      return;
    }

    // Safeguard: Check if slot is already in cart
    if (cartedSlotIds.includes(slot.id)) {
      toast({
        variant: "destructive",
        title: "Already in cart",
        description: "This facility slot is already in your cart.",
      });
      return;
    }

    // Check member validation
    if (validationError) {
      const error = validationError as any;
      const errorMessage =
        error?.data?.detail ||
        error?.data?.message ||
        "Member does not meet facility criteria";
      toast({
        variant: "destructive",
        title: "Validation failed",
        description: errorMessage,
      });
      return;
    }

    setSelectedSlot(slot);
    setSelectedAddOns([]);
    setSlotOpen(false);
  };

  const handleAddOnToggle = (addOn: any, checked: boolean) => {
    // Prevent toggling inclusive add-ons
    if (addOn.type === FacilityAddOnType.Inclusive) return;

    if (checked) {
      setSelectedAddOns((prev) => [
        ...prev,
        {
          id: addOn.id,
          name: addOn.name,
          price: addOn.price,
          type: addOn.type,
          quantity: 1,
        },
      ]);
    } else {
      setSelectedAddOns((prev) => prev.filter((item) => item.id !== addOn.id));
    }
  };

  const handleAddOnQuantityChange = (addOnId: number, quantity: number) => {
    if (quantity <= 0) {
      setSelectedAddOns((prev) => prev.filter((item) => item.id !== addOnId));
    } else {
      setSelectedAddOns((prev) =>
        prev.map((item) =>
          item.id === addOnId ? { ...item, quantity } : item,
        ),
      );
    }
  };

  const handleAddToCart = () => {
    if (!selectedSlot || !schedule || !pricing) return;

    const cartItem: FacilitySlotCartItem = {
      slot: selectedSlot,
      schedule,
      selectedAddOns: [...selectedAddOns],
      totalPrice: pricing.finalTotal,
      subtotal: pricing.subtotal,
      taxAmount: pricing.taxAmount,
      finalTotal: pricing.finalTotal,
      incomeCategoryId: schedule.general_info?.income_category_id || 0,
      taxRate: pricing.taxRate,
      taxName: pricing.taxName,
    };

    onAddToCart(cartItem);

    // Reset selections
    setSelectedFacility(null);
    setSelectedSlot(null);
    setSelectedAddOns([]);
  };

  const availableAddOns = schedule?.add_ons || [];

  console.log({ availableAddOns });
  return (
    <Card className="p-4 space-y-4">
      {/* Facility Selection */}
      <Combobox
        key={selectedFacility?.id || "empty"}
        list={facilityOptions}
        label="Select Facility"
        defaultValue={selectedFacility?.name || ""}
        setFilter={(selectedLabel: string) => {
          const facility = facilities.find((f) => f.name === selectedLabel);
          if (facility) {
            handleFacilitySelect(facility);
          }
        }}
        className="w-full"
        showLabel={false}
      />

      {/* Slot Selection */}
      {selectedFacility && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Select Slot</label>
            {!selectedMember && (
              <span className="text-[10px] text-amber-600 font-medium animate-pulse flex items-center gap-1">
                <Search className="h-2.5 w-2.5" />
                Select a member first
              </span>
            )}
          </div>
          <SlotSelector
            slots={availableSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={handleSlotSelect}
            isLoading={slotsLoading}
            action="add"
            cartedSlotIds={cartedSlotIds}
            disabled={!selectedMember}
            onMonthChange={setCurrentMonth}
            key={`slot-selector-${selectedFacility?.id || "none"}`}
          />
        </div>
      )}

      {/* Add-ons Selection */}
      {selectedSlot && availableAddOns.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Available Add-ons</h4>
          <div className="space-y-2">
            {availableAddOns.map((addOn) => {
              const selectedAddOn = selectedAddOns.find(
                (item) => item.id === addOn.id,
              );
              const isInclusive = addOn.type === FacilityAddOnType.Inclusive;

              return (
                <div
                  key={addOn.id}
                  className={cn(
                    "flex items-center justify-between p-2 border rounded",
                    isInclusive && "bg-blue-50/50 border-blue-100",
                  )}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!selectedAddOn || isInclusive}
                        onChange={(e) =>
                          handleAddOnToggle(addOn, e.target.checked)
                        }
                        disabled={isInclusive}
                        className={cn(
                          "rounded",
                          isInclusive && "cursor-not-allowed opacity-50",
                        )}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{addOn.name}</p>
                          {isInclusive && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold uppercase">
                              Inclusive
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {addOn.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{formatToPKR(addOn.price)}</span>
                    {/* add on quantity */}
                    {/* {selectedAddOn && (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddOnQuantityChange(addOn.id, selectedAddOn.quantity - 1)}
                              disabled={selectedAddOn.quantity <= 1}
                              className="h-6 w-6 p-0"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-sm w-8 text-center">{selectedAddOn.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddOnQuantityChange(addOn.id, selectedAddOn.quantity + 1)}
                              className="h-6 w-6 p-0"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        )} */}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Slot Details & Add-ons */}
      {selectedSlot && schedule && (
        <div className="space-y-4">
          {/* Slot Details */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-medium text-sm mb-2">Slot Details</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Facility:</span>
                <span>{schedule.general_info?.facility_name}</span>
              </div>
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{displayDate(selectedSlot.slot_date)}</span>
              </div>
              <div className="flex justify-between">
                <span>Time:</span>
                <span>
                  {selectedSlot.start_time} - {selectedSlot.end_time}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Duration:</span>
                <span>
                  {selectedSlot.duration_hours ??
                    (Number(selectedSlot.end_time.split(":")[0]) * 60 +
                      Number(selectedSlot.end_time.split(":")[1]) -
                      (Number(selectedSlot.start_time.split(":")[0]) * 60 +
                        Number(selectedSlot.start_time.split(":")[1]))) /
                      60}
                  h
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Breakdown */}
          {pricing && (
            <div className="bg-blue-50 p-3 rounded-lg">
              <h4 className="font-medium text-sm mb-2">Pricing Breakdown</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span>Base Price:</span>
                    {pricing.priceType === "special" && (
                      <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        {pricing.appliedPricingName || "Special"}
                      </span>
                    )}
                    {pricing.priceType === "plan" && (
                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        {pricing.appliedPricingName || "Plan"}
                      </span>
                    )}
                    {pricing.priceType === "group" && (
                      <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-bold uppercase">
                        Group
                      </span>
                    )}
                  </div>
                  <span>{formatToPKR(pricing.basePrice)}</span>
                </div>
                {selectedAddOns.length > 0 && (
                  <div className="flex justify-between">
                    <span>Add-ons:</span>
                    <span>{formatToPKR(pricing.addOnsTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatToPKR(pricing.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax ({pricing.taxRate}%):</span>
                  <span>{formatToPKR(pricing.taxAmount)}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Total:</span>
                  <span>{formatToPKR(pricing.finalTotal)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Add to Cart Button */}
          <Button
            onClick={handleAddToCart}
            className="w-full"
            disabled={!pricing || pricing.finalTotal <= 0}
          >
            Add to Cart ({formatToPKR(pricing?.finalTotal || 0)})
          </Button>
        </div>
      )}
    </Card>
  );
};

export default FacilitySlots;
